import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Landing from "@/pages/Landing";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import MyTasks from "@/pages/MyTasks";
// Legacy Finance.jsx retired 2026-02-11 — /app/finance redirects to /app/expenses-v2.
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Masters from "@/pages/Masters";
import Profile from "@/pages/Profile";
import Employees from "@/pages/Employees";
// Legacy Budgets.jsx retired 2026-02-11 — /app/budgets redirects to /app/budget-v2.
import BudgetV2 from "@/pages/BudgetV2";
import ExpensesV2 from "@/pages/ExpensesV2";
import BudgetReports from "@/pages/BudgetReports";
import BudgetAdminTools from "@/pages/BudgetAdminTools";
import Reimbursements from "@/pages/Reimbursements";
import Salary from "@/pages/Salary";
import FundReceipts from "@/pages/FundReceipts";
import Advances from "@/pages/Advances";
import UtilisationCertificate from "@/pages/UtilisationCertificate";
import UCTemplates from "@/pages/UCTemplates";
import NeedsReview from "@/pages/NeedsReview";
import BatchPay from "@/pages/BatchPay";
import Ageing from "@/pages/Ageing";
import MonthlyGrid from "@/pages/MonthlyGrid";
import CashStatement from "@/pages/CashStatement";
import Migration from "@/pages/Migration";
import ApprovalsInbox from "@/pages/ApprovalsInbox";
import MyRequests from "@/pages/MyRequests";
import OpenAdvances from "@/pages/OpenAdvances";
import RejectedReview from "@/pages/RejectedReview";
import SelfDisbursed from "@/pages/SelfDisbursed";
import Monitoring from "@/pages/Monitoring";
import SlaSettings from "@/pages/SlaSettings";
import CronRuns from "@/pages/CronRuns";
import Help from "@/pages/Help";

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<Protected />}>
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="tasks" element={<MyTasks />} />
              <Route path="finance" element={<Navigate to="/app/expenses-v2" replace state={{ retiredNotice: "The old Project Expenses module has been retired. Use Project Expenses (Budget V2)." }} />} />
              <Route path="reports" element={<Reports />} />
              <Route path="users" element={<Users />} />
              <Route path="masters" element={<Masters />} />
              <Route path="profile" element={<Profile />} />
              <Route path="employees" element={<Employees />} />
              <Route path="budgets" element={<Navigate to="/app/budget-v2" replace state={{ retiredNotice: "The old Budget module has been retired. Use Budget V2." }} />} />
              <Route path="budget-v2" element={<BudgetV2 />} />
              <Route path="expenses-v2" element={<ExpensesV2 />} />
              <Route path="budget-reports" element={<BudgetReports />} />
              <Route path="budget-admin" element={<BudgetAdminTools />} />
              <Route path="reimbursements" element={<Reimbursements />} />
              <Route path="salary" element={<Salary />} />
              <Route path="fund-receipts" element={<FundReceipts />} />
              <Route path="advances" element={<Advances />} />
              <Route path="uc" element={<Navigate to="/app/utilisation-certificate" replace />} />
              <Route path="utilisation-certificate" element={<UtilisationCertificate />} />
              <Route path="uc-templates" element={<UCTemplates />} />
              <Route path="needs-review" element={<NeedsReview />} />
              <Route path="batch-pay" element={<BatchPay />} />
              <Route path="ageing" element={<Ageing />} />
              <Route path="monthly-grid" element={<MonthlyGrid />} />
              <Route path="cash-statement" element={<CashStatement />} />
              <Route path="migration" element={<Migration />} />
              <Route path="approvals" element={<ApprovalsInbox />} />
              <Route path="my-requests" element={<MyRequests />} />
              <Route path="reports/open-advances" element={<OpenAdvances />} />
              <Route path="reports/rejected-review" element={<RejectedReview />} />
              <Route path="reports/self-disbursed" element={<SelfDisbursed />} />
              <Route path="monitoring" element={<Monitoring />} />
              <Route path="settings/sla" element={<SlaSettings />} />
              <Route path="cron" element={<CronRuns />} />
              <Route path="help" element={<Help />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
